import { useMemo } from "react";
import { Link, NavigateFunction, useLoaderData } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { classNames } from "@ct-react/core";
import { useLocaleContext, useLocaleNavigate, useLocaleUrl } from "@ct-react/locale";
import { Asset, CmsPageContent, extractCmsContent } from "@shared/graphql";
import { ArticleType } from "@shared/url";
import { CmsRouteLoaderData } from "@shared/routing";
import { actions } from "../i18n/sharable-defs";
import Button from "../components/common/button";
import SeoMetas from "../components/layout/seo-metas";
import PageHero from "../components/layout/page-elements/page-hero";
import RepeatableSection from "../components/cms/repeatable-section";
import Panel from "../components/cms/panel";
import IndexFooter from "../components/cms/index-footer";
import PrestationsPart from "../components/home/prestations";
import ServicesList from "../components/cms/services-list";
import SuggestedItems from "../components/cms/suggested-items";
import PageFoot from "../components/layout/page-elements/page-foot";
import footBackground from "../assets/home/footer-contact.png";
import "./cms.scss";

const transDefs = defineMessages({
  offMarket: { id: "cmspage.off-market-title", defaultMessage: "Nos biens off-market" },
  contact: { id: "cmspage.property-development-contact", defaultMessage: "Contactez-nous pour plus d'informations" },
});

const SINGLE_CMS_PAGE_GQL_DATA = gql`
  query SingleCmsPage($pageId: ID!) {
    cmsPage(pageId: $pageId) {
      data
    }
  }
`;

const DefaultCmsPage = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const navigate = useLocaleNavigate();
  const { currentUrl } = useLocaleUrl();
  const { cms: { pageId, ...pageConfig } } = useLoaderData<CmsRouteLoaderData>();

  const { data, loading } = useQuery<any>(SINGLE_CMS_PAGE_GQL_DATA, { ssr: false, variables: { pageId } });

  const content = useMemo<CmsPageContent | undefined>(() => {
    if (!data?.cmsPage) return undefined;
    return extractCmsContent(data.cmsPage.data, locale);
  }, [ data, locale ]);

  const withPrestations = useMemo<boolean>(() => {
    return !!currentUrl.match(`^\/${locale.basename}\/rentals\/trust-us`);
  }, [ locale, currentUrl ]);

  const withServices = useMemo<boolean>(() => {
    return !!currentUrl.match(`^\/${locale.basename}\/rentals\/.*your-property\/`) ||
      (!pageConfig.isIndex && pageConfig.category === "services");
  }, [ locale, currentUrl, pageConfig ]);

  const withOffMarketItems = useMemo<boolean>(() => {
    return !!currentUrl.match(`^\/${locale.basename}\/sales\/off-market`);
  }, [ locale, currentUrl ]);

  const withContact = useMemo<boolean>(() => {
    return pageConfig.isIndex && pageConfig.category === "property-development";
  }, [ pageConfig ])

  const wrapperClasses = classNames("cms-page", {
    "bg-inverted": pageConfig.category === "services" && pageConfig.isIndex,
    "img-inverted": pageConfig.category === "renovation" && !pageConfig.isIndex
  });

  const innerItemClasses = classNames(pageConfig.category, { index: pageConfig.isIndex });

  if (loading || !content)
     return (
       <main className={wrapperClasses}>
         <div className="wrap centered main-hero" />
       </main>);

  return (
    <main key={pageId} className={wrapperClasses}>

      <SeoMetas title={content["seo-data"]["seo-title"]}
                description={content["seo-data"]["seo-description"]}
                image={content["hero-header"]["hero-image"]?.assets.find((asset: Asset) => asset.breakpoint === "LG")?.url}
                keywords={content["seo-data"]["seo-keywords"]?.split(",").map((k: string) => k.trim()) || []} />

      <PageHero title={content["hero-header"]["hero-title"]}
                chapo={content["hero-header"]["hero-lead"]}
                background={content["hero-header"]["hero-image"].originalUrl} />

      {(content["top-info-sections-items"] || []).map((item: any, index: number) => (
        <div key={index} className="separator inner modulo-bg">
          <RepeatableSection className={innerItemClasses}
                             data={item} />
        </div>)
      )}

      {!!content["panel-items"] &&
        <section className="break-panel separator inner modulo-bg">
          <Panel className={innerItemClasses}
                 data={{ title: content["panel-group-title"], content: content["panel-items"] }} />
        </section>
      }

      {(content["bottom-info-sections-items"] || []).map((item: any, index: number) => (
        <div key={index} className="separator inner modulo-bg">
          <RepeatableSection className={innerItemClasses}
                             data={item} />
        </div>)
      )}

      {!!content["footer-links"] &&
        <div className="separator inner modulo-bg">
          <IndexFooter className={innerItemClasses}
                       data={content["footer-links"]} />
        </div>
      }

      {withPrestations &&
        <PrestationsPart className="inner separator" />
      }

      {withServices &&
        <ServicesList className="inner separator"
                      {...(pageConfig.category === "services" && !pageConfig.isIndex) && { exclusion: pageId }} />
      }

      {withOffMarketItems &&
        <SuggestedItems className="inner separator off-market-list"
                        title={intl.formatMessage(transDefs.offMarket)}
                        subTitle={content["hero-header"]["hero-title"]}
                        criteria={{ kind: ArticleType.SALE, tag: "off-market" }} />
      }

      {withContact &&
        <PageFoot background={footBackground}>
          <h2>{intl.formatMessage(transDefs.contact)}</h2>
          <Button type="button" className="link white l-spacing"
                  onClick={() => navigate("/contact/")}>
            {intl.formatMessage(actions.contact)}
          </Button>
        </PageFoot>
      }

    </main>);

};

export default DefaultCmsPage;
